import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback} from "react";

import {
    IntegerField,
    type AiGuideRelatedLinksRecommendedLinkClickedV3,
} from "@khanacademy/event-schemas";
import {View} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {styles as typographyStyles} from "@khanacademy/wonder-blocks-typography";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {useGuideState} from "@khan/guide-state";
import type {InteractionID, RelatedLink} from "@khan/guide-types";

import {FormattedText} from "../formatted-text.tsx";

import {REACTION_BADGE_SIZE} from "./constants.ts";
import {MessageReactionBadge} from "./message-reaction-badge.tsx";

interface Props {
    text: string;
    interactionId: InteractionID;
    links?: ReadonlyArray<RelatedLink>;
    shouldShowCursor?: boolean;
    isIncoming: boolean;
    contentFlagged?: boolean;
}

export const MessageContent = ({
    text,
    interactionId,
    links,
    shouldShowCursor,
    isIncoming,
    contentFlagged,
}: Props): React.ReactElement => {
    const {id: navigationId} = useGuideState("navigation");
    const ratedInteractions = useGuideState("ratedInteractions");
    const threadId = useGuideState("threadId");
    const rating = ratedInteractions.get(interactionId);

    const handleBeforeNav = useCallback(
        (href: string) => {
            const relatedLinkIndex = (links ?? []).findIndex(
                (link) => link.url === href,
            );
            const relatedLink = links?.[relatedLinkIndex];

            // Related links are links that KM pulls from our pinecone index
            // for all learnable content if they are relevant to the
            // conversation
            if (relatedLink) {
                return publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideRelatedLinksRecommendedLinkClicked",
                    eventSchemaVersion: 3,
                    aiGuideThreadId: threadId ?? "",
                    aiGuideNavigationId: navigationId,
                    aiGuideInteractionId: interactionId,
                    linkedResourceType:
                        relatedLink.contentKind as AiGuideRelatedLinksRecommendedLinkClickedV3["linkedResourceType"],
                    similarityScoreDistance: relatedLink.score,
                    relatedLinksUrlFollowed: relatedLink.url,
                    linkedResourcePosition: new IntegerField(relatedLinkIndex),
                });
            }

            // Contextual links are grabbed on the frontend by the
            // findLinksInLessonPage utility function. They are
            // included in the contextualLinks prompt variable
            return publishAnalyticsEventForMinorConversion({
                aiGuideThreadId: threadId ?? "",
                aiGuideNavigationId: navigationId,
                aiGuideInteractionId: interactionId,
                contextualLinkUrlFollowed: href,
                eventType: "AiGuideContextualLinkClicked",
                eventSchemaVersion: 2,
            });
        },
        [interactionId, links, navigationId, threadId],
    );

    return (
        <View>
            <FormattedText
                text={text}
                style={[typographyStyles.Body]}
                showCursor={shouldShowCursor}
                onBeforeNav={handleBeforeNav}
            />
            {isIncoming && rating?.sentiment && !contentFlagged && (
                <MessageReactionBadge
                    style={styles.reactionBadge}
                    reaction={rating.sentiment}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    reactionBadge: {
        position: "absolute",
        // Top is calculated by 100% bubble height (including bottom padding)
        // minus half of the icon size (24).
        top: `calc(100% + ${spacing.xSmall_8}px - ${
            REACTION_BADGE_SIZE / 2
        }px)`,
        // Left is calculated by 100% bubble width (including right padding)
        // minus half of the icon size (24).
        insetInlineStart: `calc(100% + ${spacing.medium_16}px - ${
            REACTION_BADGE_SIZE / 2
        }px)`,
    },
});
